import { api } from "./api";
import { nativePlatform } from "./components/InstallHint";
import { PUSH_ALERT_LINES, playSound, playVoiceFile } from "./audio";
import { isFeatureEnabled } from "./flags-core";
import { noteIdFromTag, pushLineFor, startRing } from "./ring";
import { refreshRoulette } from "./roulette";
import { isRouletteTag } from "../shared/roulette.ts";

/** Web Push on the client: permission, subscription, and the in-app peon sound. */

export type PushState = "unsupported" | "blocked" | "off" | "on";

/** Plays a startled peon line: a push arrived while the app is open (web or iOS app). */
export function playPushAlert(): void {
  const file = PUSH_ALERT_LINES[Math.floor(Math.random() * PUSH_ALERT_LINES.length)];
  playSound(`/audio/peons/${file}`);
}

/**
 * A push arrived while the app is open. A timed note's ring (`alarm`, or the "peon-ring" sound) rings
 * in the app (src/ring.ts: the line, then the chime looped until Stop). Any other push that names a
 * known sound says that push's own line in the original voice when it is in use
 * (shared/push-sounds.ts); without one, today's startled line.
 */
export function handlePushMessage(data: { alarm?: unknown; sound?: unknown; tag?: unknown }): void {
  // A roulette push (the spin's or the charge's, tag "roulette-<taskId>"): a page already open reads
  // the spins again, so the wheel comes up now rather than at the next load (docs/roulette.md).
  if (isRouletteTag(data.tag) && isFeatureEnabled("rouletteUI")) void refreshRoulette(() => api.roulette.list());
  if (data.alarm === true || data.sound === "peon-ring") {
    startRing(noteIdFromTag(data.tag));
    return;
  }
  const line = pushLineFor(data.sound, isFeatureEnabled("originalAssets"));
  if (line) void playVoiceFile("original", line);
  else playPushAlert();
}

// Inside the iOS app there is no Web Push, so each entry point below hands over to APNs
// (src/native-push.ts, loaded on demand so browsers never fetch the plugin). The web
// branches are untouched. The Android app has no push of its own (docs/android.md): its
// notifications are Chrome's Web Push, whose taps open the app through its verified link to the
// site, so there it reads as "unsupported" and the account sheet points to Chrome instead.
const native = () => import("./native-push");
const isIosApp = () => nativePlatform() === "ios";
const isAndroidApp = () => nativePlatform() === "android";

/**
 * Opens a page of the site in the phone's browser. In the Android app a plain link to the site
 * would stay in the app (Capacitor keeps its own host in the web view, and the verified app link
 * sends the site back to the app), so it asks the shell to hand the URL to Chrome itself.
 */
export async function openInBrowser(url: string): Promise<void> {
  if (isAndroidApp()) {
    try {
      await (await import("./native-android")).openInBrowser(url);
      return;
    } catch {
      /* fall through to a plain window.open */
    }
  }
  window.open(url, "_blank");
}

export function pushSupported(): boolean {
  if (isAndroidApp()) return false;
  if (isIosApp()) return true;
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  // main.tsx skips registration on localhost; register on demand so push can be tried in dev.
  return navigator.serviceWorker.register("/sw.js");
}

function urlBase64ToUint8Array(s: string): Uint8Array {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

export async function getPushState(): Promise<PushState> {
  if (isAndroidApp()) return "unsupported";
  if (isIosApp()) return (await native()).nativePushState();
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    return sub ? "on" : "off";
  } catch {
    return "off";
  }
}

/** Asks permission (must be called from a tap), subscribes, and stores the subscription server-side. */
export async function enablePush(name: string): Promise<PushState> {
  if (isAndroidApp()) return "unsupported";
  if (isIosApp()) return (await native()).enableNativePush(name);
  if (!pushSupported()) return "unsupported";
  const permission = await Notification.requestPermission();
  if (permission === "denied") return "blocked";
  if (permission !== "granted") return "off";
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const { key } = await api.push.publicKey();
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) as BufferSource }));
  await api.push.subscribe(sub.toJSON(), name);
  return "on";
}

export async function disablePush(): Promise<PushState> {
  if (isAndroidApp()) return "unsupported";
  if (isIosApp()) return (await native()).disableNativePush();
  if (!pushSupported()) return "unsupported";
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.push.unsubscribe(sub.endpoint).catch(() => {});
    await sub.unsubscribe();
  }
  return "off";
}

/** After a name change, re-label this device's subscription so its own tasks don't ping it. */
export async function syncPushName(name: string): Promise<void> {
  if (isAndroidApp()) return;
  if (isIosApp()) {
    try {
      await (await native()).syncNativePushName(name);
    } catch {
      /* best effort */
    }
    return;
  }
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) await api.push.subscribe(sub.toJSON(), name);
  } catch {
    /* best effort */
  }
}

/** Is the notifications permission still unasked? Only the iOS app asks this here (NotifyPrompt). */
export async function nativePushUndecided(): Promise<boolean> {
  if (!isIosApp()) return false;
  return (await native()).nativePermissionUndecided();
}

/** In the iOS app: the APNs listeners (foreground sound, taps, token rotation). No-op in a browser. */
export async function installNativePushListeners(): Promise<() => void> {
  if (!isIosApp()) return () => {};
  return (await native()).installNativePush();
}

/** Plays the push's peon line (or rings a timed note) when a push arrives while the app is open. */
export function installPushSoundListener(): () => void {
  if (!("serviceWorker" in navigator)) return () => {};
  const onMessage = (e: MessageEvent) => {
    if (e.data?.type !== "push") return;
    handlePushMessage(e.data);
  };
  navigator.serviceWorker.addEventListener("message", onMessage);
  return () => navigator.serviceWorker.removeEventListener("message", onMessage);
}
